"use client";

import React, { useCallback, useEffect, useMemo, useState, FC } from "react";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import clsx from "clsx";

import { BffComponentType } from "~/bff/ComponentsConfig";
import {
  CombinedColorFilter,
  FilterTypes,
} from "~/components/products-layout/components/products-filters/types";
import { UniversalComponent } from "~/components/universal-component/component";
import { shoppingTypeFilterComponentConfig } from "~/constants/components-configs/shop-type-filter-component-config";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useBreakpoints } from "~/hooks/use-break-points/index";

import { ColorFilter } from "./components/color-filter/component";
import { MultiFilter } from "./components/multi-filter/component";
import { PriceFilter } from "./components/price-filter/component";
import { SwatchFilter } from "./components/swatch-filter/component";
import { FILTER_CLASSES } from "./constants";
import { FilterContainer } from "./styled";
import { FilterProps } from "./types";

export const Filter: FC<FilterProps> = ({
  type = "swatch",
  onClick,
  label,
  name,
  filters,
  isExpanded,
  appliedFilters,
  filterLabels,
  shopFilter,
  shopFilterProps,
  filterLabelFromCms,
  translations,
}) => {
  const { isSm, isXs } = useBreakpoints();
  const [isOpen, setOpen] = useState(isExpanded);

  const isMobileView = isSm || isXs;

  useEffect(() => {
    setOpen(isExpanded);
  }, [isExpanded]);

  const handleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isMobileView) {
      setOpen(false);
    }
  }, [isMobileView]);

  const filter = useMemo(() => {
    return (
      <>
        {type === "multi" && (
          <MultiFilter
            onClick={onClick}
            label={label}
            appliedFilters={appliedFilters}
            filters={filters}
          />
        )}
        {type === "swatch" && (
          <SwatchFilter
            label={label}
            filters={filters}
            appliedFilters={appliedFilters}
            onClick={onClick}
          />
        )}
        {type === "color" && (
          <ColorFilter
            name={name ?? label}
            onClick={onClick}
            label={label}
            appliedFilters={appliedFilters}
            filters={filters as CombinedColorFilter[]}
          />
        )}
        {type === "price" && (
          <PriceFilter
            onClick={onClick}
            label={label}
            appliedFilters={appliedFilters}
            filters={filters}
            translations={translations}
          />
        )}
        {type === "shop" && (
          <UniversalComponent
            props={shopFilterProps}
            component={shopFilter?.component as BffComponentType}
            componentsConfigs={[shoppingTypeFilterComponentConfig]}
          />
        )}
      </>
    );
  }, [
    type,
    onClick,
    name,
    label,
    appliedFilters,
    filters,
    shopFilterProps,
    shopFilter,
    translations,
  ]);

  const checkedLabels = useMemo(() => {
    return (
      !isOpen && (
        <Box
          className={clsx({
            [FILTER_CLASSES.labelContainer]: true,
            [FILTER_CLASSES.hideOnDesktop]: type !== "swatch",
          })}
        >
          {filterLabels?.map((label) => {
            return (
              <Typography
                key={label}
                variant="body2"
                className={clsx(FILTER_CLASSES.label, "articleSmallBold")}
              >
                {label}
              </Typography>
            );
          })}
          {type === "swatch" && !filterLabels?.length && (
            <Typography
              key="all"
              variant="body2"
              className={clsx(FILTER_CLASSES.label, "articleSmallBold")}
            >
              {translations?.categoryFilterAllText || "All"}
            </Typography>
          )}
        </Box>
      )
    );
  }, [filterLabels, isOpen, type, translations?.categoryFilterAllText]);

  const nameForId = (name || "").trim().replaceAll(" ", "-").toLowerCase();

  return (
    <FilterContainer
      {...getTestAutomationProps("filter")}
      id={`filter-${nameForId}`}
      isShopFilter={type === FilterTypes.shop}
      isScrollbar={filters && filters.length > 6}
      isColorFilter={type === FilterTypes.color}
    >
      <Accordion expanded={isOpen}>
        <AccordionSummary
          classes={{ content: FILTER_CLASSES.accordionTitleContent }}
          onClick={handleOpen}
          className={FILTER_CLASSES.filterLabel}
        >
          <Typography
            variant="body1"
            className={
              isOpen
                ? clsx(FILTER_CLASSES.label, "articleSmallBold")
                : clsx(FILTER_CLASSES.label, "articleSmall")
            }
          >
            {filterLabelFromCms}
          </Typography>
          {checkedLabels}
        </AccordionSummary>
        {isOpen ? (
          <AccordionDetails className={FILTER_CLASSES.filterItemsContainer}>
            {filter}
          </AccordionDetails>
        ) : null}
      </Accordion>
    </FilterContainer>
  );
};
